import { z } from "zod/v4";
import { apiFetch, ApiError } from "@/lib/apiClient";

export const jobIds = ["currency-rates", "cleanup-stale-records", "country-images"] as const;
export const runStatuses = ["QUEUED", "RUNNING", "RETRYING", "COMPLETED", "FAILED", "SKIPPED"] as const;
export const statusLabels = { QUEUED: "Queued", RUNNING: "Running", RETRYING: "Retrying", COMPLETED: "Completed", FAILED: "Failed", SKIPPED: "Skipped" };
export const PAGE_SIZE = 25;
export const MAX_PAGE = 401;
export const jobSearchSchema = z.object({
  job: z.enum(["all", ...jobIds]).catch("all"),
  status: z.enum(["all", ...runStatuses]).catch("all"),
  page: z.coerce.number().int().min(1).max(MAX_PAGE).catch(1),
  run: z.uuid().optional().catch(undefined),
});
export type JobsSearch = z.infer<typeof jobSearchSchema>;
export type JobId = typeof jobIds[number];
const date = z.iso.datetime();
const nullableDate = date.nullable();
const count = z.number().int().nonnegative();
export const scheduleSchema = z.union([
  z.strictObject({ kind: z.literal("interval"), hours: z.number().int().min(1).max(168) }),
  z.strictObject({ kind: z.literal("daily"), time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/) }),
]);
const resultSchema = z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()]));
export const runSchema = z.object({
  id: z.uuid(), jobKey: z.enum(jobIds), trigger: z.enum(["manual", "scheduled", "cli"]), initiatedBy: z.string().nullable(),
  scheduledAt: nullableDate,
  status: z.enum(runStatuses), dispatchPending: z.boolean(), createdAt: date, startedAt: nullableDate, finishedAt: nullableDate,
  attemptsMade: count, result: resultSchema.nullable(), error: z.string().nullable(),
  attempts: z.array(z.object({ number: z.number().int().positive(), startedAt: date, finishedAt: nullableDate, result: resultSchema.nullable(), error: z.string().nullable() })),
});
export const jobSchema = z.object({
  id: z.enum(jobIds), name: z.string(), description: z.string(), enabled: z.boolean(), schedule: scheduleSchema, recommendedSchedule: scheduleSchema, timezone: z.literal("UTC"),
  syncStatus: z.enum(["applied", "pending", "error"]), syncError: z.string().nullable(), nextRunAt: nullableDate,
  updatedAt: date, updatedBy: z.string().nullable(), currentRun: runSchema.nullable(), lastRun: runSchema.nullable(),
});
export const jobsSchema = z.object({ fetchedAt: date, jobs: z.array(jobSchema).refine(jobs => jobs.length === jobIds.length && new Set(jobs.map(job => job.id)).size === jobIds.length, "Expected all maintenance jobs") });
export const runsSchema = z.object({ fetchedAt: date, job: z.enum(["all", ...jobIds]), status: z.enum(["all", ...runStatuses]), offset: count, limit: count, total: count, runs: z.array(runSchema) });
export type MaintenanceJob = z.infer<typeof jobSchema>;
export type MaintenanceRun = z.infer<typeof runSchema>;
export function runsPath(search: JobsSearch) {
  const valid = jobSearchSchema.parse(search);
  return `/admin/jobs/runs?${new URLSearchParams({ job: valid.job, status: valid.status, offset: String((valid.page - 1) * PAGE_SIZE), limit: String(PAGE_SIZE) })}`;
}
export async function getJobs(signal?: AbortSignal) { return jobsSchema.parse(await apiFetch("/admin/jobs", { signal })); }
export async function getRuns(search: JobsSearch, signal?: AbortSignal) {
  const data = runsSchema.parse(await apiFetch(runsPath(search), { signal }));
  if (data.job !== search.job || data.status !== search.status || data.offset !== (search.page - 1) * PAGE_SIZE || data.limit !== PAGE_SIZE || data.runs.length > PAGE_SIZE || data.runs.some(run => (search.job !== "all" && run.jobKey !== search.job) || (search.status !== "all" && run.status !== search.status))) throw new Error("The run history response could not be verified.");
  return data;
}
export async function getRun(runId: string, signal?: AbortSignal) {
  const id = z.uuid().parse(runId);
  const data = runSchema.parse(await apiFetch(`/admin/jobs/runs/${id}`, { signal }));
  if (data.id !== id) throw new Error("The run detail response could not be verified.");
  return data;
}
export async function saveSchedule(id: JobId, updatedAt: string, draft: { enabled: boolean; schedule: z.infer<typeof scheduleSchema> }) {
  const body = z.strictObject({ updatedAt: date, enabled: z.boolean(), schedule: scheduleSchema }).parse({ updatedAt, ...draft });
  const saved = jobSchema.parse(await apiFetch(`/admin/jobs/${z.enum(jobIds).parse(id)}/schedule`, { method: "PATCH", body: JSON.stringify(body) }));
  if (saved.id !== id) throw new Error("The saved schedule response could not be verified.");
  return saved;
}
export function isPendingRun(run: Pick<MaintenanceRun, "status"> | null | undefined) { return Boolean(run && ["QUEUED", "RUNNING", "RETRYING"].includes(run.status)); }
export function jobsErrorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if ([401, 403].includes(error.status)) return "Your administrator access could not be confirmed. Recheck access to continue.";
    if (error.status === 429) return "Too many requests. Automatic refresh is paused. Wait before refreshing manually.";
    if (error.code === "SCHEDULE_CHANGED") return "The saved schedule changed. Reload it and review your retained draft before saving again.";
    if (error.code === "JOB_ALREADY_PENDING") return "This job already has a pending run. Refresh to view its current state.";
    if (error.code === "IDEMPOTENCY_KEY_REUSED") return "This action ID was already used. Refresh the current state before requesting another run.";
    if (error.code === "JOB_RUN_NOT_FOUND") return "This run could not be found. It may no longer be retained.";
  }
  return "Maintenance jobs could not be refreshed. Check your connection and try again.";
}
export async function requestRun(id: JobId, idempotencyKey: string) {
  const body = { idempotencyKey: z.uuid().parse(idempotencyKey) };
  const run = runSchema.parse(await apiFetch(`/admin/jobs/${z.enum(jobIds).parse(id)}/runs`, { method: "POST", body: JSON.stringify(body) }));
  if (run.jobKey !== id) throw new Error("The accepted run response could not be verified.");
  return run;
}

export function isMissingRun(error: unknown) { return error instanceof ApiError && error.status === 404 && error.code === "JOB_RUN_NOT_FOUND"; }
